/* eslint-disable no-unused-vars */
import { getJssdkConfig } from "@/api/modules/userAuth";
import dynamicLoadScript from "@/scripts/dynamicLoadScript";
import { isWxwork, isDevtools, sleep } from "./utils";
import { addForwardRecordApi } from "@/api/modules/material";

const jssdkScriptUrl = "//res.wx.qq.com/open/js/jweixin-1.2.0.js";

class WxSDK {
  hasInitConfig = false;

  setUpJsApiList = [
    "hideOptionMenu",
    "hideMenuItems",
    "sendChatMessage",
    "getContext",
    "openLocation",
    "navigateToAddCustomer",
    "previewFile",
    "shareToExternalContact",
    "shareAppMessage",
    "shareWechatMessage",
    "thirdPartyOpenPage",
  ];
  // eslint-disable-next-line no-useless-constructor
  constructor() {}

  init() {
    dynamicLoadScript(jssdkScriptUrl);
  }

  // 企业微信初始化jssdk需要配置的
  initConfigOnAppStart() {
    window.wx.hideOptionMenu();
    window.wx.hideMenuItems({
      menuList: [
        "menuItem:share:appMessage",
        "menuItem:share:timeline",
        "menuItem:share:wechat",
        "menuItem:favorite",
        "menuItem:copyUrl",
        "menuItem:openWithSafari",
        "menuItem:share:email",
        "menuItem:share:appMessage",
        "menuItem:share:qq",
        "menuItem:share:weiboApp",
        "menuItem:share:facebook",
        "menuItem:share:QZone",
        "menuItem:editTag",
        "menuItem:delete",
        "menuItem:originPage",
        "menuItem:readMode",
        "menuItem:openWithQQBrowser",
      ],
    });
  }

  /**
   * 通过config注入企业微信的身份与权限
   * @param {string[]} jsApiList 需要配置使用的JS接口列表
   */
  async wxConfig(signConf, jsApiList = []) {
    await checkInvalidAccess();
    await isJssdkReady();
    return new Promise((resolve, reject) => {
      const { corpId, nonceStr, signature, timestamp } = signConf;
      if (!this.hasInitConfig) {
        // 没有初始化配置时需要加上接口权限
        jsApiList = jsApiList.concat(this.setUpJsApiList);
      }
      const _config = {
        beta: true, // 必须这么写，否则wx.invoke调用形式的jsapi会有问题
        debug: false, // 开启调试模式,调用的所有api的返回值会在客户端alert出来，若要查看传入的参数，可以在pc端打开，参数信息会通过log打出，仅在pc端时才会打印。
        appId: corpId, // 必填，企业微信的corpID
        timestamp: timestamp, // 必填，生成签名的时间戳
        nonceStr: nonceStr, // 必填，生成签名的随机串
        signature: signature, // 必填，签名，见 附录-JS-SDK使用权限签名算法
        jsApiList: jsApiList, // 必填，需要使用的JS接口列表，凡是要调用的接口都需要传进来
      };
      window.wx.config(_config);
      window.wx.ready(() => {
        resolve();
        if (!this.hasInitConfig) {
          // this.initConfigOnAppStart()
          this.hasInitConfig = true;
        }
      });
      window.wx.error((res) => reject(new Error(res.errMsg)));
    });
  }

  /**
   * 通过agentConfig注入企业微信应用的身份与权限
   * - 部分接口需要调用agentConfig才能使用
   * @param {string[]} jsApiList 需要使用的JS接口列表
   */
  async wxAgentConfig(signConf, jsApiList = []) {
    await checkInvalidAccess();
    await isJssdkReady();
    return new Promise((resolve, reject) => {
      const { agentId, nonceStr, signature, timestamp } = signConf;
      const _config = {
        corpid: global.APP_CONFIG.corpId, // 必填，企业微信的corpid，必须与当前登录的企业一致
        agentid: agentId, // 必填，企业微信的应用id （e.g. 1000247）
        timestamp: timestamp, // 必填，生成签名的时间戳
        nonceStr: nonceStr, // 必填，生成签名的随机串
        signature: signature, // 必填，签名，见附录-JS-SDK使用权限签名算法
        jsApiList: jsApiList, // 必填，需要使用的JS接口列表，凡是要调用的接口都需要传进来
      };
      window.wx.agentConfig({
        ..._config,
        success: function() {
          resolve();
        },
        fail: function(res) {
          if (res.errMsg.indexOf("function not exist") > -1) {
            reject(new Error("版本过低请升级"));
          }
          reject(new Error(res.errMsg));
        },
      });
    });
  }

  /**
   * 获取进入H5页面的入口环境
   * - 必须先成功调用agentConfig，否则调用时会报“no permission”错误
   * - config注入的是企业的身份与权限，而agentConfig注入的是应用的身份与权限。尤其是当调用者为第三方服务商时，通过config无法准确区分出调用者是哪个第三方应用，而在部分场景下，又必须严谨区分出第三方应用的身份，此时即需要通过agentConfig来注入应用的身份信息。
   */
  async config(wxJsApiList = [], agentJsApiList = []) {
    await getJssdkConfig({
      url: getUrl(),
    }).then(async (config) => {
      config = config.data || {};
      const { corpSign, agentSign } = config;
      if (!isDevtools()) {
        await this.wxConfig(corpSign, wxJsApiList);
        await this.wxAgentConfig(agentSign, agentJsApiList);
      }
    });
  }

  /**
   * 获取进入H5页面的入口环境
   * - 必须先成功调用agentConfig，否则调用时会报“no permission”错误
   */
  getContext() {
    return new Promise((resolve, reject) => {
      window.wx.invoke("getContext", {}, function(res) {
        if (res.err_msg === "getContext:ok") {
          const entry = res.entry; // 返回进入H5页面的入口类型，目前有normal、contact_profile、single_chat_tools、group_chat_tools
          resolve(entry);
        } else {
          // 错误处理
          reject(new Error(res.errMsg));
        }
      });
    });
  }

  /**
   * 获取当前外部联系人userid
   * - 从外部联系人的profile、聊天工具栏进入页面时才可成功调用该接口
   */
  getCurExternalContact() {
    return new Promise((resolve, reject) => {
      window.wx.invoke("getCurExternalContact", {}, function(res) {
        if (res.err_msg === "getCurExternalContact:ok") {
          resolve(res.userId); // 返回当前外部联系人userId
        } else {
          // 错误处理
          reject(new Error(res.errMsg));
        }
      });
    });
  }

  /**
   * 打开外部联系人聊天框
   * @param {string} externalUserId 外部联系人userId
   * @param {string} custMobie 联系人手机号 (业务中极端场景下，当前登陆人并没有添加该客户微信，此时需要提示复制此手机号添加为联系人)
   */
  openExternalUserChat(externalUserId, custMobie) {
    return new Promise((resolve, reject) => {
      window.wx.openEnterpriseChat({
        // 注意：userIds和externalUserIds至少选填一个。内部群最多2000人；外部群最多500人；如果有微信联系人，最多40人
        userIds: "", // 参与会话的企业成员列表，格式为userid1;userid2;...，用分号隔开。
        externalUserIds: externalUserId, // 参与会话的外部联系人列表，格式为userId1;userId2;…，用分号隔开。
        groupName: "", // 必填，会话名称。单聊时该参数传入空字符串""即可。
        success: resolve,
        fail: function(res) {
          if (res.errMsg.indexOf("function not exist") > -1) {
            reject(new Error("企微版本过低请升级"));
            // } else if (res.errMsg === 'openEnterpriseChat:invalid ids') {
            //   Vue.prototype.$dialog.confirm({
            //     title: '提示',
            //     message: `
            //       <div>未添加客户微信，无法使用企微联系。</div>
            //       <div>请先添加客户为联系人</div>
            //       <div style="text-align:center; color:#101010; margin-top:0.2rem">客户手机号：${custMobie}</div>
            //     `,
            //     confirmButtonText: '复制手机号',
            //     className: 'ams-cutomer-no-wechat-tip'
            //   }).then(() => {
            //     copyText(custMobie, function () {
            //       Vue.prototype.$notify({
            //         type: 'success',
            //         message: '复制成功'
            //       })
            //     })
            //   })
            //     .catch(() => {
            //       // on cancel
            //     })
          } else {
            reject(new Error(res.errMsg));
          }
        },
      });
    });
  }

  /**
   * 聊天工具栏分享消息到会话
   * @param {*} msgtype 消息类型，必填
   * @param {*} contentJson 消息对象
   * @memberof WxSDK
   */
  sendChatMessage(msgtype, contentJson, Toast) {
    if (!contentJson.imgUrl) {
      contentJson.imgUrl =
        "https://center-dev-1255598736.cos.ap-guangzhou.myqcloud.com/inc-uploads/share-default.png";
    }
    return new Promise((resolve, reject) => {
      const option = {
        msgtype: msgtype,
        [msgtype]: contentJson,
        success: () => {
          resolve(true);
          Toast && Toast.clear();
        },
        fail: () => {
          resolve(false);
          Toast && Toast.clear();
        },
      };
      window.wx.invoke("sendChatMessage", option, function(res) {
        console.log(res);
        if (res.err_msg === "sendChatMessage:ok") {
          Toast && Toast.clear();
          // 发送成功
          resolve(true);
        } else {
          Toast && Toast.clear();
          // reject(new Error('分享失败'))
          reject(res);
        }
      });
    });
  }

  /**
   * 分享接口--自定义转发到会话-转发给同事
   * * @param {*} contentJson 消息对象
   */
  shareAppMessage(contentJson) {
    return new Promise((resolve, reject) => {
      window.wx.invoke(
        "shareAppMessage",
        {
          ...contentJson,
        },
        function(res) {
          console.log("转发到会话", res);
          if (res.err_msg === "shareAppMessage:ok") {
            resolve(res);
          } else {
            reject(res);
          }
        }
      );
    });
  }

  /**
   * 客户联系--群发消息给客户
   * * @param {*} data 消息对象
   */
  shareToExternalContact(data, mediaid) {
    return new Promise((resolve, reject) => {
      if (data.type === 1) {
        wx.invoke(
          "shareToExternalContact",
          {
            text: {
              content: "", // 文本内容
            },
            attachments: [
              {
                msgtype: "image", // 消息类型，必填
                image: {
                  mediaid: "", // 图片的素材id
                  imgUrl: data.cosPath, // 图片的imgUrl,跟图片mediaid填其中一个即可
                },
              },
            ],
          },
          async (res) => {
            if (res.err_msg === "shareToExternalContact:ok") {
              await addForwardRecordApi({
                sourceId: data.id,
                sourceName: data.title,
                sourceType: 1,
                channel: 2,
              });
              resolve(res);
            } else {
              reject(res);
            }
          }
        );
      } else if (data.type === 2) {
        wx.invoke(
          "shareToExternalContact",
          {
            text: {
              content: "", // 文本内容
            },
            attachments: [
              {
                msgtype: "video", // 消息类型，必填
                video: {
                  mediaid, // 视频的素材id
                },
              },
            ],
          },
          async (res) => {
            if (res.err_msg === "shareToExternalContact:ok") {
              await addForwardRecordApi({
                sourceId: data.id,
                sourceName: data.title,
                sourceType: 1,
                channel: 2,
              });
              resolve(res);
            } else {
              reject(res);
            }
          }
        );
      } else if (data.type === 3) {
        wx.invoke(
          "shareToExternalContact",
          {
            text: {
              content: "", // 文本内容
            },
            attachments: [
              {
                msgtype: "link", // 消息类型，必填
                link: {
                  title: "", // H5消息标题
                  imgUrl: "", // H5消息封面图片URL
                  desc: "", // H5消息摘要
                  url: "", // H5消息页面url 必填
                },
              },
            ],
          },
          async (res) => {
            if (res.err_msg === "shareToExternalContact:ok") {
              await addForwardRecordApi({
                sourceId: data.id,
                sourceName: data.title,
                sourceType: 1,
                channel: 2,
              });
              resolve(res);
            } else {
              reject(res);
            }
          }
        );
      } else if (data.type === 4) {
        wx.invoke(
          "shareToExternalContact",
          {
            text: {
              content: "", // 文本内容
            },
            attachments: [
              {
                msgtype: "file", // 消息类型，必填，从3.1.12版本开始支持
                file: {
                  mediaid, // 文件的素材id，必填
                },
              },
            ],
          },
          async (res) => {
            if (res.err_msg === "shareToExternalContact:ok") {
              await addForwardRecordApi({
                sourceId: data.id,
                sourceName: data.title,
                sourceType: 1,
                channel: 2,
              });
              resolve(res);
            } else {
              reject(res);
            }
          }
        );
      }
    });
  }

  /**
   * 转发客户-大于限制-h5预览地址
   * * @param {*} data 消息对象
   */
  shareToLink(data) {
    console.log(data);
    return new Promise((resolve, reject) => {
      wx.invoke(
        "shareToExternalContact",
        {
          text: {
            content: "", // 文本内容
          },
          attachments: [
            {
              msgtype: "link", // 消息类型，必填
              link: {
                title: data.link.title, // H5消息标题
                imgUrl: data.link.imgUrl, // H5消息封面图片URL
                desc: data.link.desc, // H5消息摘要
                url: data.link.url, // H5消息页面url 必填
              },
            },
          ],
        },
        async (res) => {
          if (res.err_msg === "shareToExternalContact:ok") {
            await addForwardRecordApi({
              sourceId: data.id,
              sourceName: data.title,
              sourceType: 1,
              channel: 2,
            });
            resolve(res);
          } else {
            reject(res);
          }
        }
      );
    });
  }

  /**
   * 添加好友
   */
  addFriend() {
    return new Promise((resolve, reject) => {
      window.wx.invoke("navigateToAddCustomer", {}, function(res) {
        if (res.err_msg === "navigateToAddCustomer:ok") {
          resolve(res); // 返回当前外部联系人userId
        } else {
          // 错误处理
          reject(new Error(res.errMsg));
        }
      });
    });
  }

  /**
   * 预览文件
   */
  previewFile(fileInfo) {
    console.log("previewFile:", fileInfo);

    return new Promise((resolve, reject) => {
      wx.invoke(
        "previewFile",
        {
          url: fileInfo.url,
          name: fileInfo.name,
          size: fileInfo.size,
        },
        function(res) {
          if (res.err_msg === "previewFile:ok") {
            resolve(res);
          } else {
            reject(res);
          }
        }
      );
    });
  }

  /**
   * 任务中心-转发功能
   * * @param {*} data 消息对象
   */
  taskForwarding(data) {
    return new Promise((resolve, reject) => {
      wx.invoke(
        "shareToExternalContact",
        {
          text: {
            content: data.content, // 文本内容
          },
          attachments: data.attachments,
        },
        function(res) {
          if (res.err_msg === "shareToExternalContact:ok") {
            resolve(res);
          } else {
            reject(res);
          }
        }
      );
    });
  }

  /**
   * 话术库-转发话术功能
   * * @param {*} text 话术
   */
  speechBankForwarding(text) {
    return new Promise((resolve, reject) => {
      wx.invoke(
        "shareToExternalContact",
        {
          text: {
            content: text, // 文本内容
          },
        },
        function(res) {
          if (res.err_msg === "shareToExternalContact:ok") {
            resolve(res);
          } else {
            reject(res);
          }
        }
      );
    });
  }

  /**
   * 自定义转发到微信
   * * @param {*} data 消息对象
   */
  shareWechatMessage(data) {
    return new Promise((resolve, reject) => {
      wx.invoke(
        "shareWechatMessage",
        {
          title: data.title, // 分享标题
          desc: data.desc, // 分享描述
          link: data.link, // 分享链接
          imgUrl: data.imgUrl, // 分享封面
        },
        function(res) {
          if (res.err_msg === "shareWechatMessage:ok") {
            resolve(res);
          } else {
            reject(res);
          }
        }
      );
    });
  }
}

const isJssdkReady = async () => {
  if (window.wx) return true;
  if (!document.getElementById(jssdkScriptUrl)) {
    dynamicLoadScript(jssdkScriptUrl);
  }
  await sleep(50);
  return await isJssdkReady();
};

const checkInvalidAccess = async () => {
  if (!isWxwork()) {
    return Promise.reject(new Error("jssdk功能请在企业微信客户端中测试"));
  }
};

const getUrl = () => {
  // 兼容修复mac3.1.18版本签名后配置agentConfig报错
  const isMacintosh =
    window.navigator.userAgent.indexOf("Macintosh") !== -1 &&
    window.navigator.userAgent.indexOf("wxwork/3.1.18") !== -1;
  const url =
    isMacintosh && window.location.href.indexOf("?") === -1
      ? window.location.href.split("#")[0] + "?"
      : window.location.href.split("#")[0];
  return url;
};

export default new WxSDK();
